import { Box, Tooltip, Typography, makeStyles } from '@material-ui/core';
import {
  TableCellProps,
  WbCard,
  WbButton,
  WbCardContent,
  WbDrawer,
  WbTable,
  getTruncateMaxLinesStyle,
} from '@agilelab/plugin-wb-platform';
import { useTaxonomySelection } from '@agilelab/plugin-wb-practice-shaper';
import { DataProductRequestForm } from '@agilelab/plugin-wb-system-prototype';
import {
  PrototypeStatus,
  SystemPrototype,
  PrototypeStatusBadge,
} from '@agilelab/plugin-wb-system-prototype-common';
import { ErrorPanel } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { systemPrototypeDetailRouteRef } from '../../../../routes';
import { useSystemPrototypes } from '../../../hooks/useSystemPrototypes';
import { ChangePrototypeStatusDialog } from './ChangePrototypeStatusDialog';
import { DeletePrototypeDialog } from './DeletePrototypeDialog';
import { PrototypeActionsColumn } from './PrototypeActionsColumn';
import { useDomainsEntities } from '@agilelab/plugin-wb-builder-catalog';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useQuery } from '@tanstack/react-query';
import { parseEntityRef } from '@backstage/catalog-model';
import { expandEntityRef } from '@agilelab/plugin-wb-platform-common';
import {
  WitboostDomain,
  SystemPrototypeType,
} from '@agilelab/plugin-wb-builder-common';

const useStyles = makeStyles(
  theme => ({
    root: {},
    formControl: {
      width: '100%',
      overflow: 'auto',
      height: '100%',
      justifyContent: 'space-between',
    },
    cardActions: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    columnLabel: {
      ...getTruncateMaxLinesStyle(2, false),
    },
  }),
  { name: 'PrototypeTable' },
);

export const PrototypeTable = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);

  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);

  const { selectedTaxonomyRef } = useTaxonomySelection();

  const navigate = useNavigate();

  const [prototypeToDelete, setPrototypeToDelete] =
    useState<SystemPrototype | null>(null);

  const [deletePrototypeDialogOpen, setDeletePrototypeDialogOpen] =
    useState(false);

  const [changeStatusDialogProps, setChangeStatusDialogProps] = useState<{
    prototype: SystemPrototype;
    newStatus: PrototypeStatus;
  } | null>(null);

  const getDetailPageUrl = useRouteRef(systemPrototypeDetailRouteRef);

  const {
    data: prototypes,
    isLoading: loading,
    error,
  } = useSystemPrototypes(selectedTaxonomyRef);

  const { data: domains, error: domainsError } = useDomainsEntities();

  useQuery({
    queryKey: ['systemPrototypeTemplates'],
    queryFn: async () => {
      const { items } = await catalogApi.getEntities({
        filter: { kind: 'SystemPrototypeTemplate' },
      });
      return items;
    },
  });

  const { data: types } = useQuery({
    queryKey: ['systemPrototypeTypes'],
    queryFn: async () => {
      const { items } = await catalogApi.getEntities({
        filter: { kind: 'SystemPrototypeType' },
      });
      return items as SystemPrototypeType[];
    },
  });

  const getDomainDisplayName = useCallback(
    (domainRef: string) => {
      try {
        const domain = parseEntityRef(expandEntityRef(domainRef));
        const matchingDomain = domains?.items.find(
          d =>
            d.metadata.name === domain.name &&
            d.metadata.namespace === domain.namespace,
        );
        if (!matchingDomain) return domain.name;
        return (
          (matchingDomain as WitboostDomain).spec?.mesh?.name ?? domain.name
        );
      } catch (_e) {
        return domainRef;
      }
    },
    [domains],
  );

  const getTypeDisplayName = useCallback(
    (typeRef: string) => {
      try {
        const type = parseEntityRef(expandEntityRef(typeRef));
        const matchingType = types?.find(
          t =>
            t.metadata.name === type.name &&
            t.metadata.namespace === type.namespace,
        );
        if (!matchingType) return type.name;
        return matchingType.metadata.displayName ?? type.name;
      } catch (_e) {
        return typeRef;
      }
    },
    [types],
  );

  const columns = useMemo(() => {
    const cols: TableCellProps<SystemPrototype>[] = [
      {
        headerName: 'Name',
        field: 'name',
        fieldRender: row => (
          <Tooltip title={row.name ?? ''}>
            <Typography className={classes.columnLabel}>{row.name}</Typography>
          </Tooltip>
        ),
      },
    ];

    if (prototypes?.some(d => d.systemPrototypeType)) {
      cols.push({
        headerName: 'Type',
        field: 'systemPrototypeType',
        fieldRender: row => (
          <Tooltip title={getTypeDisplayName(row.systemPrototypeType ?? '')}>
            <Typography className={classes.columnLabel}>
              {getTypeDisplayName(row.systemPrototypeType ?? '')}
            </Typography>
          </Tooltip>
        ),
      });
    }

    if (prototypes?.some(d => d.status)) {
      cols.push({
        headerName: 'Status',
        field: 'status',
        fieldRender: row => {
          return (
            <Tooltip title={row.status ?? ''}>
              <PrototypeStatusBadge status={row.status} />
            </Tooltip>
          );
        },
      });
    }

    if (prototypes?.some(d => d.domain)) {
      cols.push({
        headerName: 'Domain',
        field: 'domain',
        fieldRender: row => (
          <Tooltip title={getDomainDisplayName(row.domain ?? '')}>
            <Typography className={classes.columnLabel}>
              {getDomainDisplayName(row.domain ?? '')}
            </Typography>
          </Tooltip>
        ),
      });
    }
    if (prototypes?.some(d => d.description)) {
      cols.push({
        headerName: 'Description',
        field: 'description',
        fieldRender: row => (
          <Tooltip title={row.description ?? ''}>
            <Typography className={classes.columnLabel}>
              {row.description}
            </Typography>
          </Tooltip>
        ),
      });
    }

    if (prototypes?.some(d => d.purpose)) {
      cols.push({
        headerName: 'Purpose',
        field: 'purpose',
        fieldRender: row => (
          <Tooltip title={row.purpose ?? ''}>
            <Typography className={classes.columnLabel}>
              {row.purpose}
            </Typography>
          </Tooltip>
        ),
      });
    }

    cols.push({
      field: 'actions',
      width: 80,
      resizable: false,
      fieldRender: row => (
        <PrototypeActionsColumn
          prototype={row}
          onDelete={p => {
            setDeletePrototypeDialogOpen(true);
            setPrototypeToDelete(p);
          }}
          onChangeStatus={(p, status) =>
            setChangeStatusDialogProps({ prototype: p, newStatus: status })
          }
        />
      ),
    });

    return cols;
  }, [
    prototypes,
    classes.columnLabel,
    getDomainDisplayName,
    getTypeDisplayName,
  ]);

  if (error || domainsError)
    return (
      <ErrorPanel
        error={
          error instanceof Error ? error : new Error('Something went wrong')
        }
      />
    );

  return (
    <WbCard
      title="Requests for Implementation"
      actions={
        <Box className={classes.cardActions}>
          <WbButton
            label="New Prototype"
            onClick={() => setCreateDrawerOpen(true)}
            icon={<AddCircleIcon />}
          />
        </Box>
      }
    >
      <WbCardContent>
        <WbTable
          stickyHeader
          styles={{
            container: {
              overflow: 'auto',
            },
          }}
          components={{
            tableLoader: { loading },
            tableContent: {
              onRowClick: row => {
                navigate(getDetailPageUrl({ id: row.id.toString() }));
              },
              columns: columns,
              rows: prototypes,
            },
          }}
        />
        <ChangePrototypeStatusDialog
          open={Boolean(changeStatusDialogProps)}
          prototype={changeStatusDialogProps?.prototype}
          newStatus={changeStatusDialogProps?.newStatus}
          onClose={() => setChangeStatusDialogProps(null)}
        />
        <DeletePrototypeDialog
          open={deletePrototypeDialogOpen}
          prototypeToDelete={prototypeToDelete}
          onClose={() => setDeletePrototypeDialogOpen(false)}
        />
        <WbDrawer
          open={createDrawerOpen}
          setOpen={setCreateDrawerOpen}
          title="New Prototype"
          PaperProps={{
            style: {
              width: '70vw',
            },
          }}
        >
          <DataProductRequestForm
            taxonomyRef={selectedTaxonomyRef}
            onCancel={() => setCreateDrawerOpen(false)}
            onSave={() => {
              setCreateDrawerOpen(false);
            }}
          />
        </WbDrawer>
      </WbCardContent>
    </WbCard>
  );
};
